import { memo, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useTitle } from 'react-use';

import { ReadOnlyBanner } from '@/components/atoms/ReadOnlyBanner/ReadOnlyBanner';
import { useAuthDeepLink } from '@/components/organisms/Dialogs/Action/SubAgent/useAuthDeepLink';
import DialogEditor from '@/components/organisms/Dialogs/DialogEditor';
import SubHeaderDialog from '@/components/organisms/SubHeader/SubHeaderDialog';
import SubNavDialog from '@/components/organisms/SubNav/SubNavDialog';
import { makeItemsName } from '@/components/organisms/SubNav/utils';
import Toolkit from '@/components/organisms/Toolkit';
import useBrains from '@/hooks/useBrains';
import useDialogNodes from '@/hooks/useDialogNodes';
import useDialogs from '@/hooks/useDialogs';
import usePrompt from '@/hooks/usePrompt';
import { cn } from '@/lib/utils';
import { selectHasError } from '@/redux/dialogAlerts/selectors';
import { clearDialogNodes, setDialog, setDialogAsync } from '@/redux/nodes/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { setDialogId } from '@/redux/session/actions';
import { selectBrainId } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

import { useNodeDeepLink } from './useNodeDeepLink';

import styles from './Dialog.module.scss';

const Dialog: React.FC = memo(() => {
  const { t } = useTranslation();
  const { dialogId } = useParams();
  const isDraft = dialogId === 'draft';
  const brainId = useSelector(selectBrainId);
  const { brain } = useBrains(brainId);
  const { dialog, saveDialog, dialogs } = useDialogs(brainId, dialogId);
  const dispatch = useDispatch();
  useTitle(t('pages.dialog', { 0: brain?.name ?? '-' }));

  useDialogNodes({ type: 'dialog' });
  useAuthDeepLink();
  useNodeDeepLink();

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );

  useSegments(PageName.DIALOG);

  const { isDirty, pendingSelection, dialog_id } = useSelector(
    (state: RootState) => ({
      isDirty: state.nodes.dirty,
      pendingSelection: state.nodes.pendingSelection,
      dialog_id: state.nodes.dialogId,
    }),
    shallowEqual
  );

  const hasDialogError = useSelector(selectHasError(dialog_id));

  usePrompt(isDirty || hasDialogError, t('prompts.leave_warning'), undefined, saveDialog);

  useEffect(() => {
    if (dialog?.dialog_id && !isDraft) {
      dispatch(setDialog(dialog));
      dispatch(setDialogId(dialog?.dialog_id));
    }
    return () => {
      if (dialog?.dialog_id) {
        dispatch(clearDialogNodes());
      }
    };
  }, [dispatch, dialog?.dialog_id, dialog, isDraft]);

  const draftInitialized = useRef(false);

  useEffect(() => {
    if (isDraft && !draftInitialized.current && dialogs) {
      const newDialogName = makeItemsName(dialogs, 'name', 'Dialog_name');
      dispatch(setDialog({ name: newDialogName, nodes: [] }));
      draftInitialized.current = true;
    } else if (!isDraft) {
      draftInitialized.current = false;
    }
  }, [brainId, dialogs, dispatch, isDraft]);

  // Update selection
  useEffect(() => {
    if (dialog?.dialog_id && dialog_id) {
      dispatch(setDialogAsync());
    }
  }, [dispatch, dialog_id, pendingSelection, dialog?.dialog_id]);

  return (
    <>
      {!canWrite && <ReadOnlyBanner />}
      <div
        className={cn(styles.wrapper, 'h-full overflow-x-hidden', { [styles.readOnly]: !canWrite })}
      >
        <SubNavDialog />
        <SubHeaderDialog />
        <DialogEditor />
        <div className={styles.toolkitContainer}>
          <Toolkit />
        </div>
      </div>
    </>
  );
});

Dialog.displayName = 'Dialog';

export default Dialog;
